// IMPORTS
import { useSelector } from 'react-redux'

// COMPONENTS
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import EditIcon from '@mui/icons-material/Edit'
import DragIcon from './DragIcon'

// UTILS
import { hasRole } from '../../../utils/utilityFuncs'

const defaultImg = 'https://lesson-images-legacy.s3-us-west-1.amazonaws.com/img/img-thumb.png'

const cardShell = {
  width: 300,
  m: '5px',
  p: 0,
  overflow: 'hidden',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(21,101,192,0.08)'
  }
}

const CompactCard = (props) => {
  const {
    imageThumbnail,
    subject,
    title,
    order,
    notDraggable,
    setModalProp
  } = props
  const { roles } = useSelector(state => state.auth.user)
  const canEdit = hasRole(['author', 'superadmin'], roles) && !notDraggable

  return (
    <Card elevation={0} sx={cardShell}>
      <Stack
        direction='row'
        sx={{
          width: '100%',
          alignItems: 'stretch',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          sx={{
            flex: '1 1 25%',
            minWidth: 0,
            px: 1,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.08)'
          }}
        >
          <Typography
            variant='caption'
            noWrap
            component='div'
            sx={{
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: '0.65rem',
              lineHeight: 1.3
            }}
          >
            {subject}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: '1 1 50%',
            minWidth: 0,
            px: 1,
            py: 0.75,
            bgcolor: 'grey.900',
            color: 'grey.100',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Stack direction='row' spacing={0.5} alignItems='center' sx={{ width: '100%' }}>
            <Typography variant='caption' sx={{ fontWeight: 600, opacity: 0.95 }}>
              Order
              <Box component='span' sx={{ fontWeight: 800, ml: 0.75, color: 'common.white' }}>
                {order}
              </Box>
            </Typography>
            <IconButton
              size='small'
              onClick={() => setModalProp(props)}
              aria-label='Edit lesson order'
              sx={{
                ml: 'auto',
                color: 'grey.300',
                '&:hover': { color: 'common.white', bgcolor: 'rgba(255,255,255,0.08)' }
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>
        {(canEdit && !notDraggable) && (
          <DragIcon sx={{ flex: '1 1 25%', minWidth: 0 }} />
        )}
      </Stack>
      <Tooltip
        title={
          <Box
            sx={{
              width: 280,
              height: 280,
              borderRadius: 1,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Box
              component='img'
              src={imageThumbnail || defaultImg}
              alt=''
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        }
        placement='top'
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'background.paper',
              p: 0.5,
              maxWidth: 'none',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 3
            }
          }
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            bgcolor: 'grey.50',
            cursor: 'default',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'grey.100' }
          }}
        >
          <Typography
            variant='caption'
            color='text.secondary'
            component='div'
            sx={{ display: 'block', mb: 0.25, fontWeight: 500, letterSpacing: '0.02em' }}
          >
            Title
          </Typography>
          <Typography variant='body2' fontWeight={600} component='div' sx={{ lineHeight: 1.35 }}>
            {title}
          </Typography>
        </Box>
      </Tooltip>
    </Card>
  )
}

export default CompactCard
